import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useMediaQuery, useTheme, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { TrendingUp, TrendingDown, Crown, Zap, ChevronRight } from 'lucide-react';
import { getCompetitorTrackingHistory } from '../../../utils/yourDataStore';
import CompetitorAnalysisService from '../../../services/CompetitorAnalysisService';
import { getSession } from '../../../utils/authUtils';

const formatDateLabel = (dateString) => {
  if (!dateString) return 'No date';
  const date = new Date(dateString);
  // Check for invalid date or epoch time (1970)
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    return 'No date';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatShortDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  // Check for invalid date or epoch time (1970)
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    return '-';
  }
  return date.toLocaleDateString('en-US', { day: 'numeric' });
};

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const TrackerContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '26px'
  }
}));

const CardsRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '12px',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px',
    marginBottom: '24px'
  }
}));

const MetricCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})(({ theme, variant }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '16px',
  padding: '18px',
  background: variant === 'ranking'
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)'
    : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
  color: '#ffffff',
  boxShadow: variant === 'ranking'
    ? '0 10px 30px rgba(99, 102, 241, 0.3)'
    : '0 10px 30px rgba(16, 185, 129, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
    backgroundSize: '200% 200%',
    animation: `${shimmer} 3s ease infinite`
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '18px',
    padding: '20px'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    padding: '24px'
  }
}));

const CardContent = styled(Box)({
  position: 'relative',
  zIndex: 1
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: '16px'
});

const CardTitleSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px'
});

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  opacity: 0.9,
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px'
  }
}));

const CardSubtitle = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  opacity: 0.7
});

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '38px',
  height: '38px',
  borderRadius: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(8px)',
  [theme.breakpoints.up('sm')]: {
    width: '40px',
    height: '40px',
    borderRadius: '11px'
  },
  [theme.breakpoints.up('md')]: {
    width: '44px',
    height: '44px',
    borderRadius: '12px'
  }
}));

const MetricRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '16px'
});

const MetricValueSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column'
});

const MetricValueContainer = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '4px'
});

const MetricPrefix = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  opacity: 0.8
});

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-1px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '40px',
    letterSpacing: '-1.5px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '42px',
    letterSpacing: '-2px'
  }
}));

const MetricChange = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'positive'
})(({ positive, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '11px',
  fontWeight: 700,
  padding: '4px 8px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255,255,255,0.2)',
  marginTop: '8px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    padding: '5px 10px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '13px'
  }
}));

const MiniChartContainer = styled(Box)(({ theme }) => ({
  width: '100px',
  height: '45px',
  [theme.breakpoints.up('sm')]: {
    width: '110px',
    height: '48px'
  },
  [theme.breakpoints.up('md')]: {
    width: '120px',
    height: '50px'
  }
}));

const ChartsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '18px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px'
  }
}));

const ChartCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})(({ theme, variant }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(229, 231, 235, 0.8)',
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '18px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    borderRadius: '20px',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)'
    }
  }
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
  flexWrap: 'wrap',
  gap: '8px',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '18px',
    gap: '12px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '20px'
  }
}));

const ChartTitleSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const ChartIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant'
})(({ variant, theme }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  background: variant === 'ranking'
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: variant === 'ranking'
    ? '0 4px 12px rgba(99, 102, 241, 0.25)'
    : '0 4px 12px rgba(16, 185, 129, 0.25)',
  [theme.breakpoints.up('sm')]: {
    width: '38px',
    height: '38px',
    borderRadius: '9px'
  },
  [theme.breakpoints.up('md')]: {
    width: '40px',
    height: '40px',
    borderRadius: '10px'
  }
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 700,
  color: '#1f2937',
  letterSpacing: '-0.02em',
  [theme.breakpoints.up('sm')]: {
    fontSize: '15px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const ChartSubtitle = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  color: '#9ca3af',
  marginTop: '1px'
});

const ViewAllButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6366f1',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.98)'
  },
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.08)'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '12px',
    padding: '6px 10px'
  },
  [theme.breakpoints.up('md')]: {
    '&:active': {
      transform: 'none'
    }
  }
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '160px',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    minHeight: '180px'
  },
  [theme.breakpoints.up('md')]: {
    minHeight: '200px'
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '32px 16px',
  backgroundColor: '#f8fafc',
  borderRadius: '16px',
  border: '2px dashed #e2e8f0',
  [theme.breakpoints.up('sm')]: {
    padding: '40px 20px',
    borderRadius: '18px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '48px 24px',
    borderRadius: '20px'
  }
}));

const EmptyIcon = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  fontSize: '28px'
});

const KeywordSelector = styled(FormControl)(({ theme }) => ({
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    minHeight: '44px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e5e7eb'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#6366f1'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#6366f1'
    }
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: '18px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '20px'
  }
}));

const CACHE_KEY = 'performanceTrackerCache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (profileId, keyword) => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const data = JSON.parse(cached);
    const cacheKey = `${profileId || 'all'}_${keyword || 'default'}`;
    const entry = data[cacheKey];
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry;
    }
  } catch (e) { }
  return null;
};

const setCachedData = (profileId, keyword, rankingData, scoreData, availableKeywords, allKeywordMetrics) => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const data = cached ? JSON.parse(cached) : {};
    const cacheKey = `${profileId || 'all'}_${keyword || 'default'}`;
    data[cacheKey] = { rankingData, scoreData, availableKeywords, allKeywordMetrics, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (e) { }
};

// Summary Table Styles
const SummaryCard = styled(Box)(({ theme }) => ({
  marginTop: '16px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(229, 231, 235, 0.8)',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    marginTop: '20px',
    padding: '20px',
    borderRadius: '18px'
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '24px',
    padding: '24px',
    borderRadius: '20px'
  }
}));

const SummaryHeader = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
}));

const SummaryTable = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0 8px',
  marginTop: '-8px'
});

const SummaryTh = styled('th')({
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
});

const SummaryTr = styled('tr')({
  backgroundColor: '#f8fafc',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    transform: 'scale(1.01)'
  }
});

const SummaryTd = styled('td')({
  padding: '16px',
  fontSize: '14px',
  color: '#374151',
  fontWeight: 500,
  borderTop: '1px solid transparent',
  borderBottom: '1px solid transparent',
  '&:first-of-type': {
    borderTopLeftRadius: '12px',
    borderBottomLeftRadius: '12px'
  },
  '&:last-of-type': {
    borderTopRightRadius: '12px',
    borderBottomRightRadius: '12px'
  }
});

const RankBadge = styled(Box)(({ rank }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  borderRadius: '6px',
  backgroundColor: rank <= 3 ? '#ecfdf5' : rank <= 10 ? '#fffbeb' : '#fef2f2',
  color: rank <= 3 ? '#059669' : rank <= 10 ? '#d97706' : '#dc2626',
  fontWeight: 700,
  fontSize: '13px'
}));

const ScoreBadge = styled(Typography)(({ score }) => ({
  fontWeight: 700,
  color: score >= 70 ? '#059669' : score >= 40 ? '#d97706' : '#dc2626'
}));

const PerformanceTracker = ({ profileId, businessName }) => {
  const [rankingData, setRankingData] = useState([]);
  const [scoreData, setScoreData] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [allKeywordMetrics, setAllKeywordMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartWidth, setChartWidth] = useState(280);
  const rankingChartRef = useRef(null);
  const scoreChartRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Responsive chart width
  useEffect(() => {
    const updateChartWidth = () => {
      if (rankingChartRef.current) {
        const containerWidth = rankingChartRef.current.offsetWidth;
        const padding = isMobile ? 32 : 48;
        setChartWidth(Math.max(250, containerWidth - padding));
      }
    };

    updateChartWidth();

    const resizeObserver = new ResizeObserver(updateChartWidth);
    if (rankingChartRef.current) {
      resizeObserver.observe(rankingChartRef.current);
    }

    window.addEventListener('resize', updateChartWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateChartWidth);
    };
  }, [isMobile]);

  // Load cached data instantly on mount
  useEffect(() => {
    const cached = getCachedData(profileId, selectedKeyword);
    if (cached) {
      setRankingData(cached.rankingData);
      setScoreData(cached.scoreData);
      setAvailableKeywords(cached.availableKeywords || []);
      if (cached.allKeywordMetrics) setAllKeywordMetrics(cached.allKeywordMetrics);
      setHasData(cached.rankingData.length > 0);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!profileId) {
      setHasData(false);
      setIsLoading(false);
      return;
    }
    // Load cached data for new profile immediately
    const cached = getCachedData(profileId, '');
    if (cached) {
      setRankingData(cached.rankingData);
      setScoreData(cached.scoreData);
      setAvailableKeywords(cached.availableKeywords || []);
      if (cached.allKeywordMetrics) setAllKeywordMetrics(cached.allKeywordMetrics);
      if (cached.availableKeywords?.length > 0 && !selectedKeyword) {
        setSelectedKeyword(cached.availableKeywords[0]);
      }
      setHasData(cached.rankingData.length > 0);
    } else {
      setSelectedKeyword('');
      setAvailableKeywords([]);
      setRankingData([]);
      setScoreData([]);
      setHasData(false);
    }
  }, [profileId, businessName]);

  useEffect(() => {
    // Instantly load cached data for keyword change
    const cached = getCachedData(profileId, selectedKeyword);
    if (cached) {
      setRankingData(cached.rankingData);
      setScoreData(cached.scoreData);
      setHasData(cached.rankingData.length > 0);
      setIsLoading(false);
    }

    const loadCompetitorData = async () => {
      try {
        const session = getSession();
        let userId = session?.id || session?.user?.id;

        // Check if admin is viewing a client's profile
        try {
          const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
          if (adminAccessDataRaw) {
            const adminAccessData = JSON.parse(adminAccessDataRaw);
            if (adminAccessData?.targetUserId) {
              userId = adminAccessData.targetUserId;
            }
          }
        } catch (e) {
          // Ignore parse errors
        }

        if (!userId) {
          setHasData(false);
          setIsLoading(false);
          return;
        }

        // Prioritize location_id (profileId) for accurate user-specific data lookup
        const queryParams = { limit: 500, offset: 0 };
        if (profileId) {
          // Use location_id as primary filter (user_id + location_id ensures user gets their own data)
          queryParams.location_id = profileId;
        }
        if (businessName) {
          // Also include business_name for additional filtering if available
          queryParams.business_name = businessName;
        }
        console.log('[PerformanceTracker] 🔍 DEBUG - Fetching with:', {
          userId,
          profileId,
          businessName,
          queryParams,
          isAdminView: !!sessionStorage.getItem('adminAccessData')
        });
        const response = await CompetitorAnalysisService.getHistory(userId, queryParams);
        console.log('[PerformanceTracker] 🔍 DEBUG - API response:', {
          success: response?.success,
          analysesCount: response?.data?.analyses?.length,
          rawResponse: response
        });

        if (response.success && response.data.analyses.length > 0) {
          const dbAnalyses = response.data.analyses;

          const history = dbAnalyses
            .filter(analysis => analysis.analysis_date) // Filter out entries with null/undefined dates
            .map(analysis => {
              const analysisDate = new Date(analysis.analysis_date);
              // Skip entries with invalid dates (before year 2000)
              if (isNaN(analysisDate.getTime()) || analysisDate.getFullYear() < 2000) {
                return null;
              }
              return {
                rank: analysis.my_business_rank,
                score: analysis.my_business_score,
                keyword: analysis.keyword,
                businessName: analysis.business_name,
                date: analysis.analysis_date,
                timestamp: analysisDate.getTime(),
                profileId: analysis.location_id
              };
            })
            .filter(entry => entry !== null); // Remove null entries

          const keywords = [...new Set(history.map(entry => entry.keyword).filter(k => k))];
          setAvailableKeywords(keywords);

          if (!selectedKeyword && keywords.length > 0) {
            setSelectedKeyword(keywords[0]);
          }

          const profileFilteredHistory = profileId
            ? history.filter(entry => !entry.profileId || entry.profileId === profileId)
            : history;

          const filteredHistory = selectedKeyword
            ? profileFilteredHistory.filter(entry => entry.keyword === selectedKeyword)
            : profileFilteredHistory;

          if (filteredHistory.length > 0) {
            const sortedHistory = [...filteredHistory].sort((a, b) => a.timestamp - b.timestamp);
            const recentHistory = sortedHistory.slice(-7);

            const ranking = recentHistory.map((entry) => ({
              day: formatDateLabel(entry.date),
              shortDay: formatShortDate(entry.date),
              rank: entry.rank || 0
            }));

            const scores = recentHistory.map((entry) => ({
              day: formatDateLabel(entry.date),
              shortDay: formatShortDate(entry.date),
              score: entry.score || 0
            }));

            setRankingData(ranking);
            setScoreData(scores);
            setHasData(true);
            setIsLoading(false);
            setCachedData(profileId, selectedKeyword, ranking, scores, keywords);
          } else {
            setHasData(false);
            setIsLoading(false);
          }
        } else {
          const localHistory = getCompetitorTrackingHistory(profileId);

          if (localHistory && localHistory.length > 0) {
            const keywords = [...new Set(localHistory.map(entry => entry.keyword).filter(k => k))];
            setAvailableKeywords(keywords);

            if (!selectedKeyword && keywords.length > 0) {
              setSelectedKeyword(keywords[0]);
            }

            const filteredHistory = selectedKeyword
              ? localHistory.filter(entry => entry.keyword === selectedKeyword)
              : localHistory;

            if (filteredHistory.length > 0) {
              const sortedHistory = [...filteredHistory].reverse();
              const recentHistory = sortedHistory.slice(-7);

              const ranking = recentHistory.map((entry) => ({
                day: formatDateLabel(entry.date),
                shortDay: formatShortDate(entry.date),
                rank: entry.rank || 0
              }));

              const scores = recentHistory.map((entry) => ({
                day: formatDateLabel(entry.date),
                shortDay: formatShortDate(entry.date),
                score: entry.score || 0
              }));

              setRankingData(ranking);
              setScoreData(scores);
              setHasData(true);
              setIsLoading(false);
              // Calculate metrics for all keywords (fallback logic)
              const allMetrics = keywords.map(kw => {
                const kwHistory = localHistory.filter(entry => entry.keyword === kw);
                const sortedKwHistory = [...kwHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
                const latest = sortedKwHistory[sortedKwHistory.length - 1];
                const previous = sortedKwHistory.length > 1 ? sortedKwHistory[sortedKwHistory.length - 2] : null;

                return {
                  keyword: kw,
                  rank: latest?.rank || 0,
                  score: latest?.score || 0,
                  rankChange: previous ? (previous.rank - latest.rank) : 0,
                  scoreChange: previous ? (latest.score - previous.score) : 0,
                  lastUpdated: latest?.date
                };
              });
              setAllKeywordMetrics(allMetrics);
              setCachedData(profileId, selectedKeyword, ranking, scores, keywords, allMetrics);
            } else {
              setHasData(false);
              setIsLoading(false);
            }
          } else {
            setHasData(false);
            setIsLoading(false);
          }
        }
      } catch (error) {
        try {
          const localHistory = getCompetitorTrackingHistory(profileId);
          if (localHistory && localHistory.length > 0) {
            const keywords = [...new Set(localHistory.map(entry => entry.keyword).filter(k => k))];
            setAvailableKeywords(keywords);

            if (!selectedKeyword && keywords.length > 0) {
              setSelectedKeyword(keywords[0]);
            }

            const filteredHistory = selectedKeyword
              ? localHistory.filter(entry => entry.keyword === selectedKeyword)
              : localHistory;

            if (filteredHistory.length > 0) {
              const sortedHistory = [...filteredHistory].reverse();
              const recentHistory = sortedHistory.slice(-7);

              const ranking = recentHistory.map((entry) => ({
                day: formatDateLabel(entry.date),
                shortDay: formatShortDate(entry.date),
                rank: entry.rank || 0
              }));

              const scores = recentHistory.map((entry) => ({
                day: formatDateLabel(entry.date),
                shortDay: formatShortDate(entry.date),
                score: entry.score || 0
              }));

              setRankingData(ranking);
              setScoreData(scores);
              setHasData(true);
              setIsLoading(false);
              setRankingData(ranking);
              setScoreData(scores);
              setHasData(true);
              setIsLoading(false);

              // Calculate metrics for all keywords (fallback logic)
              const allMetrics = keywords.map(kw => {
                const kwHistory = localHistory.filter(entry => entry.keyword === kw).reverse(); // localHistory is likely typically reverse sorted or we need to check, assuming similar structure logic
                // Actually localHistory usually comes sorted or we just sort it to be safe
                const sortedKwHistory = [...kwHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
                const latest = sortedKwHistory[sortedKwHistory.length - 1];
                const previous = sortedKwHistory.length > 1 ? sortedKwHistory[sortedKwHistory.length - 2] : null;

                return {
                  keyword: kw,
                  rank: latest?.rank || 0,
                  score: latest?.score || 0,
                  rankChange: previous ? (previous.rank - latest.rank) : 0,
                  scoreChange: previous ? (latest.score - previous.score) : 0,
                  lastUpdated: latest?.date
                };
              });
              setAllKeywordMetrics(allMetrics);
              setCachedData(profileId, selectedKeyword, ranking, scores, keywords, allMetrics);
            } else {
              setHasData(false);
              setIsLoading(false);
            }
          } else {
            setHasData(false);
            setIsLoading(false);
          }
        } catch (fallbackError) {
          setHasData(false);
          setIsLoading(false);
        }
      }
    };

    loadCompetitorData();
  }, [selectedKeyword, profileId, businessName]);

  const currentRank = rankingData.length > 0 ? rankingData[rankingData.length - 1].rank : 0;
  const initialRank = rankingData.length > 0 ? rankingData[0].rank : 0;
  const rankChange = initialRank - currentRank;

  const currentScore = scoreData.length > 0 ? scoreData[scoreData.length - 1].score : 0;
  const initialScore = scoreData.length > 0 ? scoreData[0].score : 0;
  const scoreChange = currentScore - initialScore;

  const iconSize = isMobile ? 18 : 22;
  const trendIconSize = isMobile ? 12 : 14;

  if (!hasData && !isLoading) {
    return (
      <TrackerContainer>
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <Typography sx={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
            No Competitor Analysis Data Yet
          </Typography>
          <Typography sx={{ fontSize: isMobile ? '13px' : '14px', color: '#9ca3af', maxWidth: '360px', margin: '0 auto' }}>
            Run competitor analysis on the Business Profile page to see your ranking and score trends here.
          </Typography>
        </EmptyState>
      </TrackerContainer>
    );
  }

  return (
    <TrackerContainer>
      {availableKeywords.length > 0 && (
        <KeywordSelector fullWidth size="small">
          <InputLabel>Select Keyword</InputLabel>
          <Select
            value={selectedKeyword}
            label="Select Keyword"
            onChange={(e) => setSelectedKeyword(e.target.value)}
          >
            {availableKeywords.map((keyword) => (
              <MenuItem key={keyword} value={keyword}>
                🔑 {keyword}
              </MenuItem>
            ))}
          </Select>
        </KeywordSelector>
      )}

      {/* Metric Cards Row */}
      <CardsRow>
        <MetricCard variant="ranking">
          <CardContent>
            <CardHeader>
              <CardTitleSection>
                <CardTitle>Current Ranking</CardTitle>
                <CardSubtitle>Position for "{selectedKeyword}"</CardSubtitle>
              </CardTitleSection>
              <IconWrapper>
                <Crown size={iconSize} color="#ffffff" />
              </IconWrapper>
            </CardHeader>

            <MetricRow>
              <MetricValueSection>
                <MetricValueContainer>
                  <MetricPrefix>#</MetricPrefix>
                  <MetricValue>{currentRank}</MetricValue>
                </MetricValueContainer>
                <MetricChange positive={rankChange > 0}>
                  {rankChange > 0 ? <TrendingUp size={trendIconSize} /> : <TrendingDown size={trendIconSize} />}
                  {Math.abs(rankChange)} {rankChange > 0 ? 'up' : 'down'}
                </MetricChange>
              </MetricValueSection>

              {/* Mini Sparkline */}
              <MiniChartContainer>
                {rankingData.length > 0 && (
                  <LineChart
                    width={isMobile ? 100 : 120}
                    height={isMobile ? 45 : 50}
                    series={[{
                      data: rankingData.map(d => d.rank),
                      color: 'rgba(255,255,255,0.8)',
                      showMark: false,
                      area: false
                    }]}
                    xAxis={[{ scaleType: 'point', data: rankingData.map((_, i) => i) }]}
                    yAxis={[{ reverse: true }]}
                    margin={{ left: 5, right: 5, top: 5, bottom: 5 }}
                    sx={{
                      '& .MuiLineElement-root': { strokeWidth: 2 }
                    }}
                    leftAxis={null}
                    bottomAxis={null}
                  />
                )}
              </MiniChartContainer>
            </MetricRow>
          </CardContent>
        </MetricCard>

        <MetricCard variant="score">
          <CardContent>
            <CardHeader>
              <CardTitleSection>
                <CardTitle>Performance Score</CardTitle>
                <CardSubtitle>Optimization for "{selectedKeyword}"</CardSubtitle>
              </CardTitleSection>
              <IconWrapper>
                <Zap size={iconSize} color="#ffffff" />
              </IconWrapper>
            </CardHeader>

            <MetricRow>
              <MetricValueSection>
                <MetricValueContainer>
                  <MetricValue>{currentScore}</MetricValue>
                </MetricValueContainer>
                <MetricChange positive={scoreChange > 0}>
                  {scoreChange > 0 ? <TrendingUp size={trendIconSize} /> : <TrendingDown size={trendIconSize} />}
                  +{Math.abs(scoreChange)} pts
                </MetricChange>
              </MetricValueSection>

              {/* Mini Sparkline */}
              <MiniChartContainer>
                {scoreData.length > 0 && (
                  <LineChart
                    width={isMobile ? 100 : 120}
                    height={isMobile ? 45 : 50}
                    series={[{
                      data: scoreData.map(d => d.score),
                      color: 'rgba(255,255,255,0.8)',
                      showMark: false,
                      area: true
                    }]}
                    xAxis={[{ scaleType: 'point', data: scoreData.map((_, i) => i) }]}
                    margin={{ left: 5, right: 5, top: 5, bottom: 5 }}
                    sx={{
                      '& .MuiLineElement-root': { strokeWidth: 2 },
                      '& .MuiAreaElement-root': { fill: 'rgba(255,255,255,0.2)' }
                    }}
                    leftAxis={null}
                    bottomAxis={null}
                  />
                )}
              </MiniChartContainer>
            </MetricRow>
          </CardContent>
        </MetricCard>
      </CardsRow>

      {/* Charts Grid - Both charts visible */}
      <ChartsGrid>
        {/* Ranking Area Chart */}
        <ChartCard variant="ranking">
          <ChartHeader>
            <ChartTitleSection>
              <ChartIconBox variant="ranking">
                <Crown size={isMobile ? 16 : 18} color="#ffffff" />
              </ChartIconBox>
              <Box>
                <ChartTitle>Ranking History</ChartTitle>
                <ChartSubtitle>Lower is better</ChartSubtitle>
              </Box>
            </ChartTitleSection>
            <ViewAllButton>
              Details <ChevronRight size={14} />
            </ViewAllButton>
          </ChartHeader>

          <ChartWrapper ref={rankingChartRef}>
            <LineChart
              width={chartWidth}
              height={isMobile ? 160 : isTablet ? 180 : 200}
              series={[{
                data: rankingData.map(d => d.rank),
                color: '#6366f1',
                area: false,
                curve: 'linear'
              }]}
              xAxis={[{
                scaleType: 'point',
                data: rankingData.map(d => isMobile ? d.shortDay : d.day),
                tickLabelStyle: {
                  fontSize: isMobile ? 9 : 11
                }
              }]}
              yAxis={[{
                reverse: true,
                min: 0
              }]}
              margin={{
                left: isMobile ? 35 : 45,
                right: isMobile ? 10 : 20,
                top: 20,
                bottom: isMobile ? 30 : 40
              }}
              grid={{ horizontal: true, vertical: false }}
            />
          </ChartWrapper>
        </ChartCard>

        {/* Score Area Chart */}
        <ChartCard variant="score">
          <ChartHeader>
            <ChartTitleSection>
              <ChartIconBox variant="score">
                <Zap size={isMobile ? 16 : 18} color="#ffffff" />
              </ChartIconBox>
              <Box>
                <ChartTitle>Score Trend</ChartTitle>
                <ChartSubtitle>Higher is better</ChartSubtitle>
              </Box>
            </ChartTitleSection>
            <ViewAllButton>
              Details <ChevronRight size={14} />
            </ViewAllButton>
          </ChartHeader>

          <ChartWrapper ref={scoreChartRef}>
            <LineChart
              width={chartWidth}
              height={isMobile ? 160 : isTablet ? 180 : 200}
              series={[{
                data: scoreData.map(d => d.score),
                color: '#10b981',
                area: true,
                curve: 'linear'
              }]}
              xAxis={[{
                scaleType: 'point',
                data: scoreData.map(d => isMobile ? d.shortDay : d.day),
                tickLabelStyle: {
                  fontSize: isMobile ? 9 : 11
                }
              }]}
              yAxis={[{
                min: 0
              }]}
              margin={{
                left: isMobile ? 35 : 45,
                right: isMobile ? 10 : 20,
                top: 20,
                bottom: isMobile ? 30 : 40
              }}
              grid={{ horizontal: true, vertical: false }}
            />
          </ChartWrapper>
        </ChartCard>
      </ChartsGrid>

      {/* All Keywords Summary Table */}
      {allKeywordMetrics.length > 0 && (
        <SummaryCard>
          <SummaryHeader>
            <Crown size={20} color="#6366f1" />
            Keyword Performance Summary
          </SummaryHeader>
          <Box sx={{ overflowX: 'auto' }}>
            <SummaryTable>
              <thead>
                <tr>
                  <SummaryTh>Keyword</SummaryTh>
                  <SummaryTh>Rank</SummaryTh>
                  <SummaryTh>Score</SummaryTh>
                  <SummaryTh>Trend</SummaryTh>
                  <SummaryTh>Last Updated</SummaryTh>
                </tr>
              </thead>
              <tbody>
                {allKeywordMetrics.map((metric, idx) => (
                  <SummaryTr key={idx} onClick={() => setSelectedKeyword(metric.keyword)} style={{ cursor: 'pointer' }}>
                    <SummaryTd>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{metric.keyword}</Typography>
                        {metric.keyword === selectedKeyword && (
                          <Box sx={{ fontSize: '10px', bgcolor: '#e0e7ff', color: '#4338ca', px: 1, borderRadius: '4px' }}>Active</Box>
                        )}
                      </Box>
                    </SummaryTd>
                    <SummaryTd>
                      <RankBadge rank={metric.rank}>#{metric.rank}</RankBadge>
                    </SummaryTd>
                    <SummaryTd>
                      <ScoreBadge score={metric.score}>{metric.score}/100</ScoreBadge>
                    </SummaryTd>
                    <SummaryTd>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {metric.rankChange > 0 ? (
                          <TrendingUp size={16} color="#10b981" />
                        ) : metric.rankChange < 0 ? (
                          <TrendingDown size={16} color="#ef4444" />
                        ) : (
                          <Box sx={{ width: 16, height: 2, bgcolor: '#d1d5db' }} />
                        )}
                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                          {metric.rankChange > 0 ? `+${metric.rankChange}` : metric.rankChange}
                        </Typography>
                      </Box>
                    </SummaryTd>
                    <SummaryTd sx={{ color: '#9ca3af', fontSize: '12px' }}>
                      {metric.lastUpdated ? (() => {
                        const date = new Date(metric.lastUpdated);
                        if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
                          return 'N/A';
                        }
                        return date.toLocaleDateString();
                      })() : 'N/A'}
                    </SummaryTd>
                  </SummaryTr>
                ))}
              </tbody>
            </SummaryTable>
          </Box>
        </SummaryCard>
      )}
    </TrackerContainer>
  );
};

export default PerformanceTracker;
